import { useState, useEffect, useCallback, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  MessageSquare,
  Image,
  RefreshCw,
  ChevronDown,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  HelpCircle,
  Loader2,
  FileCheck,
  Palette,
  PauseCircle,
  Filter,
  FolderOpen,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import ImageGallery from "@/components/ImageGallery";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import Sidebar from "@/components/Sidebar";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default icons mapping
const ICON_MAP = {
  Clock,
  AlertCircle,
  FileCheck,
  Palette,
  XCircle,
  HelpCircle,
  CheckCircle,
  PauseCircle,
};

// Filter Dropdown Component
function FilterDropdown({ label, options, selectedValues, onApply, onClear }) {
  const [tempSelected, setTempSelected] = useState(selectedValues);
  const [open, setOpen] = useState(false);

  const handleToggle = (value) => {
    setTempSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleApply = () => {
    onApply(tempSelected);
    setOpen(false);
  };

  const handleClear = () => {
    setTempSelected([]);
    onClear();
    setOpen(false);
  };

  const hasFilters = selectedValues.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-9 px-3 ${hasFilters ? "border-primary bg-primary/5" : ""}`}
        >
          {label}
          {hasFilters && (
            <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
              {selectedValues.length}
            </Badge>
          )}
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="p-3 space-y-2">
          {options.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`filter-${label}-${option.value}`}
                checked={tempSelected.includes(option.value)}
                onCheckedChange={() => handleToggle(option.value)}
              />
              <label
                htmlFor={`filter-${label}-${option.value}`}
                className="text-sm cursor-pointer flex-1"
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between p-3 border-t bg-muted/30">
          <Button variant="ghost" size="sm" onClick={handleClear}>
            Clear
          </Button>
          <Button size="sm" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Orders() {
  const navigate = useNavigate();
  const [expiringPhones, setExpiringPhones] = useState(new Set());
  const [expiredPhones, setExpiredPhones] = useState(new Set());
  const [unreadPhones, setUnreadPhones] = useState(new Set());

  useEffect(() => {
    const fetchExpiring = async () => {
      try {
        // Fetch all 3 in parallel for speed
        const [expiringResp, unreadResp, expiredResp] = await Promise.allSettled([
          axios.get(`${API}/conversations/expiring?hours=4`),
          axios.get(`${API}/conversations/unread-phones`),
          axios.get(`${API}/conversations/expired-phones`)
        ]);
        
        if (expiringResp.status === 'fulfilled')
          setExpiringPhones(new Set((expiringResp.value.data.conversations || []).map(c => c.phone_number)));
        if (unreadResp.status === 'fulfilled')
          setUnreadPhones(new Set(unreadResp.value.data.phones || []));
        if (expiredResp.status === 'fulfilled')
          setExpiredPhones(new Set(expiredResp.value.data.phones || []));
      } catch (e) {}
    };
    fetchExpiring();
    const interval = setInterval(fetchExpiring, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const [orders, setOrders] = useState([]);
  const [sessionFilter, setSessionFilter] = useState('all'); // 'all', 's1', 's2plus'
  const [expiringSoonFilter, setExpiringSoonFilter] = useState(false);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [imageGalleryOrderId, setImageGalleryOrderId] = useState(null);

  // Dynamic dropdown options from API
  const [dropdownOptions, setDropdownOptions] = useState({
    order_id_status: [],
    design_status: [],
    account: [],
    rto_flag: [],
    product_details: [],
  });

  // Filter states
  const [orderStatusFilter, setOrderStatusFilter] = useState([]);
  const [designStatusFilter, setDesignStatusFilter] = useState([]);
  const [accountFilter, setAccountFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState(""); // "today" or "monthly" or ""
  const [customDate, setCustomDate] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [pendingDateFrom, setPendingDateFrom] = useState(null);
  const [pendingDateTo, setPendingDateTo] = useState(null);
  const [activeStatCard, setActiveStatCard] = useState(""); // Track which card is active
  const activeStatCardRef = useRef("");

  // Form state for create/edit
  const [formData, setFormData] = useState({
    phone_number: "",
    order_id: "",
    customer_name: "",
    account: "",
    product_details: "",
    design_status: "EMPTY",
    order_id_status: "PENDING",
    remarks: "",
    rto_flag: "",
    extra_request: "",
    image_folder: "",
  });

  // Fetch dropdown options from API
  const fetchDropdownOptions = async () => {
    try {
      const response = await axios.get(`${API}/dropdown-options`);
      const options = {};
      response.data.forEach((config) => {
        options[config.field_name] = config.options || [];
      });
      setDropdownOptions(options);
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  useEffect(() => {
    fetchDropdownOptions();
    fetchOrders();
    fetchStats();
    const statsInterval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(statsInterval);
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [searchQuery, orderStatusFilter, designStatusFilter, accountFilter, dateFilter, customDate, sessionFilter, expiringSoonFilter, expiringPhones]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      // Check if any filter is applied
      const hasFilters = searchQuery || orderStatusFilter.length > 0 || designStatusFilter.length > 0 || accountFilter.length > 0 || (dateFilter && dateFilter !== 'today');
      // Use smaller limit when date filter is applied
      const limit = expiringSoonFilter ? 5000 : (dateFilter === 'custom' && customDate) ? 500 : (hasFilters ? 5000 : 500);
      let dateParam = expiringSoonFilter ? '' : (hasFilters ? '' : '&date=today');
      if (dateFilter === 'today') dateParam = '&date=today';
      if (dateFilter === 'custom' && customDate) {
        dateParam = `&date=${customDate}`;
        if (customDateTo) dateParam += `&date_to=${customDateTo}`;
      }
      const accountParam = accountFilter.length === 1 ? `&account=${accountFilter[0]}` : '';
      const orderStatusParam = orderStatusFilter.length > 0 ? orderStatusFilter.map(s => `&order_status=${s}`).join('') : '';
      const response = await axios.get(`${API}/orders?limit=${limit}${dateParam}${accountParam}${orderStatusParam}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`);
      let filteredOrders = response.data;

      // Apply client-side filters
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredOrders = filteredOrders.filter(
          (o) =>
            o.phone_number?.toLowerCase().includes(query) ||
            o.customer_name?.toLowerCase().includes(query) ||
            o.order_id?.toLowerCase().includes(query)
        );
      }

      if (orderStatusFilter.length > 0) {
        filteredOrders = filteredOrders.filter((o) =>
          orderStatusFilter.includes(o.order_id_status)
        );
      }

      if (designStatusFilter.length > 0) {
        filteredOrders = filteredOrders.filter((o) =>
          designStatusFilter.includes(o.design_status)
        );
      }

      if (accountFilter.length > 0) {
        filteredOrders = filteredOrders.filter((o) =>
          accountFilter.includes(o.account)
        );
      }


      // Date filtering
      if (dateFilter === "today") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        filteredOrders = filteredOrders.filter((o) => {
          const orderDate = new Date(o.created_at);
          return orderDate >= today;
        });
      } else if (dateFilter === "monthly") {
        const monthStart = new Date();
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);
        filteredOrders = filteredOrders.filter((o) => {
          const orderDate = new Date(o.created_at);
          return orderDate >= monthStart;
        });
      }

      // Filter by expiring if active
      if (activeStatCardRef?.current === "expiring" || activeStatCard === "expiring") {
        try {
          const expResp = await axios.get(`${API}/conversations/expiring?hours=4`);
          const phones = new Set((expResp.data.conversations || []).map(c => c.phone_number));
          setExpiringPhones(phones);
          filteredOrders = filteredOrders.filter(o => phones.has(o.phone_number));
        } catch(e) {}
      }

      // Session filter
      if (sessionFilter === 's1') {
        filteredOrders = filteredOrders.filter(o => o.session_id === 'S1');
      } else if (sessionFilter === 's2plus') {
        filteredOrders = filteredOrders.filter(o => o.session_id && o.session_id !== 'S1');
      }

      // Expiring soon filter
      if (expiringSoonFilter) {
        filteredOrders = filteredOrders.filter(o => expiringPhones.has(o.phone_number));
      }

      setOrders(filteredOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, orderStatusFilter, designStatusFilter, accountFilter, dateFilter, customDate, sessionFilter, expiringSoonFilter, expiringPhones]);


  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/orders/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleCreateOrder = async () => {
    if (!formData.phone_number) {
      toast.error("Phone number is required");
      return;
    }

    try {
      await axios.post(`${API}/orders`, formData);
      toast.success("Order created successfully");
      setCreateDialogOpen(false);
      resetForm();
      fetchOrders();
      fetchStats();
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to create order");
    }
  };

  const handleUpdateOrder = async () => {
    if (!selectedOrder) return;

    try {
      const response = await axios.put(`${API}/orders/${selectedOrder.id}`, formData);
      const updatedOrder = response.data;
      
      // Update order in place without changing sequence
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === selectedOrder.id ? { ...order, ...updatedOrder } : order
        )
      );
      
      toast.success("Order updated successfully");
      setEditDialogOpen(false);
      setSelectedOrder(null);
      resetForm();
      fetchStats(); // Only refresh stats, not the full list
    } catch (error) {
      console.error("Error updating order:", error);
      const msg = error.response?.data?.detail || "Failed to update order";
      toast.error(msg);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm("Are you sure you want to delete this order?")) return;

    try {
      await axios.delete(`${API}/orders/${orderId}`);
      
      // Remove order from list without re-fetching
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      
      toast.success("Order deleted");
      fetchStats(); // Only refresh stats
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order");
    }
  };

  const handleFieldUpdate = async (orderId, field, value) => {
    try {
      const response = await axios.put(`${API}/orders/${orderId}`, { [field]: value });
      const updatedOrder = response.data;
      
      // Update order in place without changing sequence
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, ...updatedOrder } : order
        )
      );
      
      fetchStats(); // Only refresh stats
    } catch (error) {
      console.error("Error updating:", error);
      toast.error("Failed to update");
    }
  };

  const resetForm = () => {
    setFormData({
      phone_number: "",
      order_id: "",
      customer_name: "",
      account: "",
      product_details: "",
      design_status: "EMPTY",
      order_id_status: "PENDING",
      remarks: "",
      rto_flag: "",
      extra_request: "",
      image_folder: "",
    });
  };

  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setFormData({
      phone_number: order.phone_number || "",
      order_id: order.order_id || "",
      customer_name: order.customer_name || "",
      account: order.account || "",
      product_details: order.product_details || "",
      design_status: order.design_status || "EMPTY",
      order_id_status: order.order_id_status || "PENDING",
      remarks: order.remarks || "",
      rto_flag: order.rto_flag || "",
      extra_request: order.extra_request || "",
      image_folder: order.image_folder || "",
    });
    setEditDialogOpen(true);
  };

  const clearAllFilters = () => {
    setOrderStatusFilter([]);
    setDesignStatusFilter([]);
    setAccountFilter([]);
    setDateFilter("");
    setActiveStatCard(""); activeStatCardRef.current = "";
    setSearchQuery("");
  };

  // Handle stat card click to filter
  const handleStatCardClick = (cardType, filterValue) => {
    // If clicking the same card, toggle it off
    if (activeStatCard === cardType) {
      clearAllFilters();
      return;
    }

    // Clear other filters first
    setOrderStatusFilter([]);
    setDesignStatusFilter([]);
    setAccountFilter([]);
    setDateFilter("");
    setSearchQuery("");
    setActiveStatCard(cardType);
    activeStatCardRef.current = cardType;

    // Apply the specific filter based on card type
    switch (cardType) {
      case "expiring":
        // Fetch expiring phones then fetch all orders and filter
        axios.get(`${API}/conversations/expiring?hours=4`).then(async resp => {
          const phones = new Set((resp.data.conversations || []).map(c => c.phone_number));
          setExpiringPhones(phones);
          // Fetch all orders then filter
          const ordersResp = await axios.get(`${API}/orders`);
          const allOrders = ordersResp.data.orders || ordersResp.data || [];
          const filtered = allOrders.filter(o => phones.has(o.phone_number));
          setOrders(filtered);
        }).catch(() => {});
        break;
      case "monthly":
        setDateFilter("monthly");
        break;
      case "today":
        setDateFilter("today");
        break;
      case "order_pending":
        setOrderStatusFilter(["PENDING"]);
        break;
      case "order_hold":
        setOrderStatusFilter(["HOLD"]);
        break;
      case "order_check":
        setOrderStatusFilter(["CHECK"]);
        break;
      case "design_empty":
        setDesignStatusFilter(["EMPTY"]);
        break;
      case "design_pending":
        setDesignStatusFilter(["PENDING"]);
        break;
      case "design_ready":
        setDesignStatusFilter(["READY"]);
        break;
      default:
        break;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM d, hh:mm aa");
    } catch {
      return "N/A";
    }
  };

  const getDesignStatusBadge = (status) => {
    const option = (dropdownOptions.design_status || []).find((o) => o.value === status);
    if (!option) return <Badge variant="outline">{status || "Empty"}</Badge>;
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  const getOrderIdStatusBadge = (status) => {
    const option = (dropdownOptions.order_id_status || []).find((o) => o.value === status);
    if (!option) return <Badge variant="outline">{status || "Pending"}</Badge>;
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  const hasActiveFilters = orderStatusFilter.length > 0 || designStatusFilter.length > 0 || accountFilter.length > 0 || dateFilter !== "" || activeStatCard !== "";

  return (
    <div className="flex h-screen overflow-hidden" data-testid="orders-page">
      {/* Sidebar */}
      <Sidebar stats={stats} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Order Database</h1>
            <p className="text-sm text-muted-foreground">
              Manage and track all customer orders
            </p>
          </div>
          {/* Half ID / Full ID counts - center */}
          <div className="flex items-center gap-2">
            {stats.order_status_half_id > 0 && (
              <div
                onClick={() => setOrderStatusFilter(prev => 
                  prev.includes('HALF_ID') ? prev.filter(x => x !== 'HALF_ID') : [...prev, 'HALF_ID']
                )}
                style={{
                  background: orderStatusFilter.includes('HALF_ID') ? '#fde047' : '#fef9c3',
                  border:'1px solid #fde047', borderRadius:'20px', padding:'3px 10px',
                  fontSize:'12px', fontWeight:600, color:'#854d0e', cursor:'pointer'
                }}
              >
                Half ID: {stats.order_status_half_id}
              </div>
            )}
            {stats.order_status_full_id > 0 && (
              <div
                onClick={() => setOrderStatusFilter(prev =>
                  prev.includes('FULL_ID') ? prev.filter(x => x !== 'FULL_ID') : [...prev, 'FULL_ID']
                )}
                style={{
                  background: orderStatusFilter.includes('FULL_ID') ? '#86efac' : '#dcfce7',
                  border:'1px solid #86efac', borderRadius:'20px', padding:'3px 10px',
                  fontSize:'12px', fontWeight:600, color:'#15803d', cursor:'pointer'
                }}
              >
                Full ID: {stats.order_status_full_id}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => { fetchOrders(); fetchStats(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <div style={{display:'flex', border:'1px solid #d1d5db', borderRadius:'20px', overflow:'hidden', fontSize:'12px', fontWeight:600}}>
              {[['all','All'],['s1','S1'],['s2plus','S2+']].map(([val, label]) => (
                <button key={val} onClick={() => setSessionFilter(val)} style={{
                  padding:'4px 10px', cursor:'pointer', border:'none',
                  background: sessionFilter === val ? (val==='all' ? '#dbeafe' : val==='s1' ? '#dcfce7' : '#ffedd5') : 'transparent',
                  color: sessionFilter === val ? (val==='all' ? '#1d4ed8' : val==='s1' ? '#15803d' : '#c2410c') : '#6b7280',
                  borderRight: val !== 's2plus' ? '1px solid #d1d5db' : 'none'
                }}>{label}</button>
              ))}
            </div>
            <button
              onClick={() => setExpiringSoonFilter(prev => !prev)}
              style={{
                padding:'4px 10px', borderRadius:'20px', fontSize:'12px',
                fontWeight:600, border:'1px solid', cursor:'pointer',
                borderColor: expiringSoonFilter ? '#ca8a04' : '#d1d5db',
                background: expiringSoonFilter ? '#fef9c3' : 'transparent',
                color: expiringSoonFilter ? '#854d0e' : '#6b7280'
              }}
            >
              ⏰ Expire Soon {expiringSoonFilter && expiringPhones.size > 0 ? `(${expiringPhones.size})` : ''}
            </button>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-order-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Order
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Create New Order</DialogTitle>
                  <DialogDescription>
                    Manually create a new order record
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone Number *</Label>
                      <Input
                        placeholder="+919876543210"
                        value={formData.phone_number}
                        onChange={(e) =>
                          setFormData({ ...formData, phone_number: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Customer Name</Label>
                      <Input
                        placeholder="Customer name"
                        value={formData.customer_name}
                        onChange={(e) =>
                          setFormData({ ...formData, customer_name: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Order ID</Label>
                      <Input
                        placeholder="Enter order ID"
                        value={formData.order_id}
                        onChange={(e) =>
                          setFormData({ ...formData, order_id: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Account</Label>
                      <Select
                        value={formData.account || "NONE"}
                        onValueChange={(value) =>
                          setFormData({ ...formData, account: value === "NONE" ? "" : value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                        <SelectContent>
                          {(dropdownOptions.account || []).map((opt) => (
                            <SelectItem key={opt.value || "NONE"} value={opt.value || "NONE"}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Product Details</Label>
                    <Select
                      value={formData.product_details || "NONE"}
                      onValueChange={(value) =>
                        setFormData({ ...formData, product_details: value === "NONE" ? "" : value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {(dropdownOptions.product_details || []).map((opt) => (
                          <SelectItem key={opt.value || "NONE"} value={opt.value || "NONE"}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Remarks</Label>
                    <Textarea
                      placeholder="Any notes about this order..."
                      value={formData.remarks}
                      onChange={(e) =>
                        setFormData({ ...formData, remarks: e.target.value })
                      }
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateOrder}>Create Order</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        {/* Stats Cards - Clickable for filtering */}
        <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {/* Total Monthly Orders */}
          <Card 
            className={`shadow-soft cursor-pointer transition-all hover:scale-105 hover:shadow-md ${activeStatCard === "monthly" ? "ring-2 ring-primary ring-offset-2" : ""}`}
            onClick={() => handleStatCardClick("monthly")}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.monthly_orders || 0}</div>
              <div className="text-xs text-muted-foreground">Total Monthly</div>
            </CardContent>
          </Card>

          {/* ORDER STATUS - PENDING */}
          <Card 
            className={`shadow-soft bg-amber-50 border-amber-200 cursor-pointer transition-all hover:scale-105 hover:shadow-md ${activeStatCard === "order_pending" ? "ring-2 ring-amber-500 ring-offset-2" : ""}`}
            onClick={() => handleStatCardClick("order_pending")}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-700">{stats.order_status_pending || 0}</div>
              <div className="text-xs text-muted-foreground font-medium">Order Status</div>
              <div className="text-xs font-semibold text-amber-600">PENDING</div>
            </CardContent>
          </Card>

          {/* ORDER STATUS - HOLD */}
          <Card 
            className={`shadow-soft bg-orange-50 border-orange-200 cursor-pointer transition-all hover:scale-105 hover:shadow-md ${activeStatCard === "order_hold" ? "ring-2 ring-orange-500 ring-offset-2" : ""}`}
            onClick={() => handleStatCardClick("order_hold")}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-orange-700">{stats.order_status_hold || 0}</div>
              <div className="text-xs text-muted-foreground font-medium">Order Status</div>
              <div className="text-xs font-semibold text-orange-600">HOLD</div>
            </CardContent>
          </Card>

          {/* ORDER STATUS - CHECK (AI Processed) */}
          <Card 
            className={`shadow-soft bg-blue-50 border-blue-200 cursor-pointer transition-all hover:scale-105 hover:shadow-md ${activeStatCard === "order_check" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
            onClick={() => handleStatCardClick("order_check")}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-700">{stats.order_status_check || 0}</div>
              <div className="text-xs text-muted-foreground font-medium">Order Status</div>
              <div className="text-xs font-semibold text-blue-600">CHECK</div>
            </CardContent>
          </Card>

          {/* DESIGN STATUS - EMPTY */}
          <Card 
            className={`shadow-soft bg-gray-50 border-gray-200 cursor-pointer transition-all hover:scale-105 hover:shadow-md ${activeStatCard === "design_empty" ? "ring-2 ring-gray-500 ring-offset-2" : ""}`}
            onClick={() => handleStatCardClick("design_empty")}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-gray-700">{stats.design_status_empty || 0}</div>
              <div className="text-xs text-muted-foreground font-medium">Design Status</div>
              <div className="text-xs font-semibold text-gray-600">EMPTY</div>
            </CardContent>
          </Card>

          {/* DESIGN STATUS - PENDING */}
          <Card 
            className={`shadow-soft bg-yellow-50 border-yellow-200 cursor-pointer transition-all hover:scale-105 hover:shadow-md ${activeStatCard === "design_pending" ? "ring-2 ring-yellow-500 ring-offset-2" : ""}`}
            onClick={() => handleStatCardClick("design_pending")}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-700">{stats.design_status_pending || 0}</div>
              <div className="text-xs text-muted-foreground font-medium">Design Status</div>
              <div className="text-xs font-semibold text-yellow-600">PENDING</div>
            </CardContent>
          </Card>

          {/* DESIGN STATUS - READY */}
          <Card 
            className={`shadow-soft bg-blue-50 border-blue-200 cursor-pointer transition-all hover:scale-105 hover:shadow-md ${activeStatCard === "design_ready" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
            onClick={() => handleStatCardClick("design_ready")}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-700">{stats.design_status_ready || 0}</div>
              <div className="text-xs text-muted-foreground font-medium">Design Status</div>
              <div className="text-xs font-semibold text-blue-600">READY</div>
            </CardContent>
          </Card>

          {/* Today Orders */}
          <Card 
            className={`shadow-soft bg-emerald-50 border-emerald-200 cursor-pointer transition-all hover:scale-105 hover:shadow-md ${activeStatCard === "today" ? "ring-2 ring-emerald-500 ring-offset-2" : ""}`}
            onClick={() => handleStatCardClick("today")}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-700">{stats.today_orders || 0}</div>
              <div className="text-xs text-muted-foreground">Today Orders</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Section */}
        <div className="px-6 pb-4">
          <div className="flex items-center gap-4 flex-wrap p-4 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Filter className="h-4 w-4" />
              Filter by:
            </div>

            {/* Order Status Filter */}
            <FilterDropdown
              label="Order Status"
              options={dropdownOptions.order_id_status || []}
              selectedValues={orderStatusFilter}
              onApply={(values) => { setActiveStatCard(""); activeStatCardRef.current = ""; setOrderStatusFilter(values); }}
              onClear={() => { setActiveStatCard(""); activeStatCardRef.current = ""; setOrderStatusFilter([]); }}
            />

            {/* Design Status Filter */}
            <FilterDropdown
              label="Design Status"
              options={dropdownOptions.design_status || []}
              selectedValues={designStatusFilter}
              onApply={(values) => { setActiveStatCard(""); activeStatCardRef.current = ""; setDesignStatusFilter(values); }}
              onClear={() => { setActiveStatCard(""); activeStatCardRef.current = ""; setDesignStatusFilter([]); }}
            />

            {/* Account Filter */}
            <FilterDropdown
              label="Account"
              options={(dropdownOptions.account || []).filter((o) => o.value !== "")}
              selectedValues={accountFilter}
              onApply={(values) => { setActiveStatCard(""); activeStatCardRef.current = ""; setAccountFilter(values); }}
              onClear={() => { setActiveStatCard(""); activeStatCardRef.current = ""; setAccountFilter([]); }}
            />

            {/* Date Range Filter */}
            <div className="flex items-center gap-1">
              <DatePicker
                selectsRange
                startDate={pendingDateFrom}
                endDate={pendingDateTo}
                onChange={(dates) => {
                  const [start, end] = dates;
                  setPendingDateFrom(start);
                  setPendingDateTo(end);
                }}
                placeholderText="Select date range"
                dateFormat="dd/MM/yyyy"
                className="h-9 px-3 text-sm border rounded-md bg-background cursor-pointer w-48"
                isClearable={false}
              />
              {pendingDateFrom && (
                <>
                  <button
                    className="h-9 px-3 text-xs bg-primary text-white rounded-md hover:bg-primary/90 font-medium"
                    onClick={() => {
                      if (pendingDateFrom) {
                        const fmt = (d) => {
                          const y = d.getFullYear();
                          const m = String(d.getMonth()+1).padStart(2,'0');
                          const day = String(d.getDate()).padStart(2,'0');
                          return `${y}-${m}-${day}`;
                        };
                        setCustomDate(fmt(pendingDateFrom));
                        setCustomDateTo(pendingDateTo ? fmt(pendingDateTo) : fmt(pendingDateFrom));
                        setDateFilter("custom");
                        setActiveStatCard(""); activeStatCardRef.current = "";
                      }
                    }}
                  >Apply</button>
                  <button
                    className="h-9 px-2 text-xs border rounded-md hover:bg-muted"
                    onClick={() => { setPendingDateFrom(null); setPendingDateTo(null); setCustomDate(""); setCustomDateTo(""); setDateFilter(""); }}
                  >✕</button>
                </>
              )}
            </div>
            {/* Order Count */}
            <div className="flex items-center gap-1 px-3 h-9 bg-primary/10 rounded-md text-sm font-medium text-primary border border-primary/20">
              {orders.length} orders
            </div>
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                className="pl-9 h-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-orders"
              />
            </div>

            {/* Clear All */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Orders Table */}
        <div className="flex-1 px-6 pb-6 overflow-hidden">
          <Card className="h-full flex flex-col shadow-soft">
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : orders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Package className="h-16 w-16 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No orders found</p>
                  <p className="text-sm text-muted-foreground/70">
                    {hasActiveFilters ? "Try adjusting your filters" : "Orders will appear here when customers send messages"}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[70px]">Session</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead className="min-w-[180px]">
                        <div className="flex items-center gap-1">
                          Order ID
                          <button
                            onClick={() => {
                              const ids = orders
                                .filter(o => o.order_id && o.order_id.trim())
                                .map(o => o.order_id.trim())
                                .join('\n');
                              navigator.clipboard.writeText(ids);
                              toast.success(`Copied ${orders.filter(o => o.order_id && o.order_id.trim()).length} order IDs!`);
                            }}
                            style={{background:'transparent', border:'none', cursor:'pointer', color:'#6b7280', padding:'2px'}}
                            title="Copy all order IDs"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableHead>
                      <TableHead>Order Status</TableHead>
                      
                      <TableHead>Product</TableHead>
                      <TableHead>Design Status</TableHead>
                      <TableHead>Images</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead>Chat</TableHead>
                      <TableHead>Print Design</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => {
                      const isExpired = expiredPhones.has(order.phone_number);
                      const isExpiring = expiringPhones.has(order.phone_number);
                      const isUnread = unreadPhones.has(order.phone_number);
                      // DEBUG: test with index
                      const rowStyle = isExpired 
                        ? {borderLeft:'4px solid #f87171', background:'#fff1f1'} 
                        : isExpiring 
                        ? {borderLeft:'4px solid #fbbf24', background:'#fefce8'} 
                        : isUnread 
                        ? {borderLeft:'4px solid #60a5fa', background:'#eff6ff'} 
                        : {};
                      // Log for debug
                      if(isExpiring) console.log('EXPIRING ROW:', order.phone_number);
                      if(isExpired) console.log('EXPIRED ROW:', order.phone_number);
                      if(isUnread) console.log('UNREAD ROW:', order.phone_number);
                      return (
                      <TableRow key={order.id} data-testid={`order-row-${order.id}`} style={rowStyle} className={isExpired||isExpiring||isUnread ? "[&>td]:!bg-transparent" : ""}>
                        {/* Session */}
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="font-mono text-xs">
                              {order.session_id}
                            </Badge>
                            {order.active_session === "Active" && (
                              <span 
                                className={`inline-block w-2 h-2 rounded-full ${expiringPhones.has(order.phone_number) ? 'bg-yellow-400' : 'bg-emerald-500'}`}
                                title={expiringPhones.has(order.phone_number) ? "Expiring soon - reply needed!" : "Active"}
                              ></span>
                            )}
                          </div>
                        </TableCell>

                        {/* Phone / Customer */}
                        <TableCell>
                          <div className="font-medium text-sm">{order.customer_name || "Unknown"}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {order.phone_number}
                          </div>
                        </TableCell>

                        {/* Order ID - Editable */}
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <Input
                              className="h-7 w-44 text-xs font-mono"
                              placeholder="Enter ID"
                              defaultValue={order.order_id || ""}
                              onBlur={(e) => {
                                if (e.target.value !== order.order_id) {
                                  handleFieldUpdate(order.id, "order_id", e.target.value);
                                }
                              }}
                            />
                            {order.order_id_updated_at && <span className="text-[9px] text-gray-400">{new Date(order.order_id_updated_at).toLocaleDateString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</span>}
                          </div>
                        </TableCell>

                        {/* Order ID Status */}
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="h-7 px-2 flex items-center gap-1 cursor-pointer" type="button">
                                {getOrderIdStatusBadge(order.order_id_status)}
                                <ChevronDown className="h-3 w-3 text-slate-400" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuLabel>Order Status</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {(dropdownOptions.order_id_status || []).map((option) => (
                                <DropdownMenuItem
                                  key={option.value}
                                  onClick={() => handleFieldUpdate(order.id, "order_id_status", option.value)}
                                  className="cursor-pointer !bg-transparent hover:!bg-transparent focus:!bg-transparent data-[highlighted]:!bg-transparent"
                                  style={{backgroundColor: 'transparent'}}
                                >
                                  <Badge className={`${option.color} mr-2`}>{option.label}</Badge>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {order.order_id_status_updated_at && <span className="text-[9px] text-gray-400">{new Date(order.order_id_status_updated_at).toLocaleDateString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</span>}
                          </div>
                        </TableCell>



                        {/* Product Details - Dropdown */}
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="h-7 px-2 flex items-center gap-1 cursor-pointer" type="button">
                                {order.product_details ? (
                                  <Badge variant="outline" className="font-mono text-xs">{order.product_details}</Badge>
                                ) : (
                                  <span className="text-muted-foreground text-xs">-</span>
                                )}
                                <ChevronDown className="h-3 w-3 text-slate-400" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuLabel>Product Details</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {(dropdownOptions.product_details || []).map((option) => (
                                <DropdownMenuItem
                                  key={option.value || "empty"}
                                  onClick={() => handleFieldUpdate(order.id, "product_details", option.value)}
                                  className="cursor-pointer !bg-transparent hover:!bg-transparent focus:!bg-transparent data-[highlighted]:!bg-transparent" style={{backgroundColor: "transparent"}}
                                >
                                  {option.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>

                        {/* Design Status */}
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="h-7 px-2 flex items-center gap-1 cursor-pointer" type="button">
                                {getDesignStatusBadge(order.design_status)}
                                <ChevronDown className="h-3 w-3 text-slate-400" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuLabel>Design Status</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {(dropdownOptions.design_status || []).map((option) => (
                                <DropdownMenuItem
                                  key={option.value}
                                  onClick={() => handleFieldUpdate(order.id, "design_status", option.value)}
                                  className="cursor-pointer !bg-transparent hover:!bg-transparent focus:!bg-transparent data-[highlighted]:!bg-transparent" style={{backgroundColor: "transparent"}}
                                >
                                  <Badge className={`${option.color} mr-2`}>{option.label}</Badge>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {order.design_status_updated_at && <span className="text-[9px] text-gray-400">{new Date(order.design_status_updated_at).toLocaleDateString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</span>}
                        </TableCell>

                        {/* Images - Clickable to open gallery */}
                        <TableCell>
                          <button
                            className="h-7 px-2 flex items-center gap-1 cursor-pointer"
                            onClick={async () => {
                              setImageGalleryOrderId(order.id);
                              // Refresh real count
                              try {
                                const resp = await axios.get(`${API}/orders/${order.id}/image-count`);
                                setOrders(prev => prev.map(o => o.id === order.id ? {...o, total_image_count: resp.data.count} : o));
                              } catch(e) {}
                            }}
                            data-testid={`view-images-${order.id}`}
                          >
                            <FolderOpen className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-sm text-blue-600">{order.total_image_count}</span>
                          </button>
                        </TableCell>

                        {/* Remarks - Editable */}
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <Input
                              className="h-7 w-24 text-xs"
                              placeholder="Remarks"
                              defaultValue={order.remarks || ""}
                              onBlur={(e) => {
                                if (e.target.value !== order.remarks) {
                                  handleFieldUpdate(order.id, "remarks", e.target.value);
                                }
                              }}
                            />
                            {order.remarks_updated_at && (
                              <span className="text-[9px] text-gray-400">
                                {new Date(order.remarks_updated_at).toLocaleDateString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}
                              </span>
                            )}
                          </div>
                        </TableCell>

                        {/* Chat - Link to conversation */}
                        <TableCell>
                          {order.phone_number ? (
                            <button
                              className="h-7 px-2 flex items-center cursor-pointer"
                              onClick={() => window.open(`/dashboard?phone=${order.phone_number}`, '_blank')}
                              title="Open conversation"
                            >
                              <MessageSquare className="h-4 w-4 text-primary" />
                            </button>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>

                        {/* Print Design */}
                        <TableCell>
                          {order.print_generated_at ? (
                            <div className="flex flex-col items-center text-center">
                              <span className="text-xs font-semibold text-emerald-600">✅ YES</span>
                              <span className="text-[9px] text-gray-400">{new Date(order.print_generated_at).toLocaleString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', hour12:true})}</span>
                            </div>
                          ) : (
                            <div className="text-center"><span className="text-xs text-red-500 font-semibold">❌ NO</span></div>
                          )}
                        </TableCell>

                        {/* Date */}
                        <TableCell>
                          <div className="text-xs text-muted-foreground">{formatDate(order.first_message_date)}</div>
                        </TableCell>

                        {/* Actions */}
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(order)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit All
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDeleteOrder(order.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ); })}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
            <DialogDescription>
              Update order details for {selectedOrder?.phone_number}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input value={formData.phone_number} disabled />
              </div>
              <div className="space-y-2">
                <Label>Customer Name</Label>
                <Input
                  placeholder="Customer name"
                  value={formData.customer_name}
                  onChange={(e) =>
                    setFormData({ ...formData, customer_name: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Order ID</Label>
                <Input
                  placeholder="Enter order ID"
                  value={formData.order_id}
                  onChange={(e) =>
                    setFormData({ ...formData, order_id: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Order ID Status</Label>
                <Select
                  value={formData.order_id_status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, order_id_status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(dropdownOptions.order_id_status || []).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account</Label>
                <Select
                  value={formData.account || "NONE"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, account: value === "NONE" ? "" : value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {(dropdownOptions.account || []).map((opt) => (
                      <SelectItem key={opt.value || "NONE"} value={opt.value || "NONE"}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Design Status</Label>
                <Select
                  value={formData.design_status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, design_status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(dropdownOptions.design_status || []).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Product Details</Label>
              <Select
                value={formData.product_details || "NONE"}
                onValueChange={(value) =>
                  setFormData({ ...formData, product_details: value === "NONE" ? "" : value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {(dropdownOptions.product_details || []).map((opt) => (
                    <SelectItem key={opt.value || "NONE"} value={opt.value || "NONE"}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Image Folder Link</Label>
              <Input
                placeholder="Folder link..."
                value={formData.image_folder}
                onChange={(e) =>
                  setFormData({ ...formData, image_folder: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                placeholder="Any notes about this order..."
                value={formData.remarks}
                onChange={(e) =>
                  setFormData({ ...formData, remarks: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>RTO Flag</Label>
                <Select
                  value={formData.rto_flag || "NONE"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, rto_flag: value === "NONE" ? "" : value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="-" />
                  </SelectTrigger>
                  <SelectContent>
                    {(dropdownOptions.rto_flag || []).map((opt) => (
                      <SelectItem key={opt.value || "NONE"} value={opt.value || "NONE"}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Extra Request</Label>
                <Select
                  value={formData.extra_request}
                  onValueChange={(value) =>
                    setFormData({ ...formData, extra_request: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="-" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">-</SelectItem>
                    <SelectItem value="YES">YES</SelectItem>
                    <SelectItem value="NO">NO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateOrder}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Gallery Modal */}
      <ImageGallery
        orderId={imageGalleryOrderId}
        open={!!imageGalleryOrderId}
        onClose={() => {
          setImageGalleryOrderId(null);
          // Don't auto-refresh to preserve scroll position
        }}
        onImagesUpdated={async () => {
          // Refresh only the affected order's count
          if (imageGalleryOrderId) {
            try {
              const resp = await axios.get(`${API}/orders/${imageGalleryOrderId}/image-count`);
              setOrders(prev => prev.map(o => o.id === imageGalleryOrderId ? {...o, total_image_count: resp.data.count} : o));
            } catch(e) {
              fetchOrders();
            }
          }
        }}
      />
    </div>
  );
}
